'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { type MarketId, MarketUtils } from '@morpho-org/blue-sdk';
import { getIsPositionUnhealthy, ZERO } from '@morpho-org/dapp-core';
import { Avatar, DataList } from '@morpho-org/ui-v6/client';
import { Skeleton, Tag } from '@morpho-org/ui-v6/server';
import { cn } from '@morpho-org/ui-v6/utils';
import { formatters, getDigitsFromPrice } from '@morpho-org/ui-v6/utils';
import { skipToken, useQuery } from '@tanstack/react-query';
import { type Address, isAddressEqual, parseEther } from 'viem';
import type { MarketPosition } from '@/lib/procedures/marketPosition.contract';
import type { MarketPositionMarket, PositionToken } from '@/lib/procedures/marketPositionMarket.contract';
import { AutoDeleverageButton } from '@/components/common/AutoDeleverage/AutoDeleverageButton';
import { useAutoDeleverage } from '@/components/common/AutoDeleverage/hooks/useAutoDeleverage';
import { useIsAutoDeleverageEnabled } from '@/components/common/AutoDeleverage/hooks/useIsAutoDeleverageEnabled';
import { ProtectedLltv, ProtectedLltvLabel, ProtectedValueIndicator, useProtectedLltv } from '@/components/common/AutoDeleverage/ProtectedLltv';
import { SubSectionTitle } from '@/components/common/SubSectionTitle';
import { useWeb3Context } from '@/contexts/Web3Context';
import { convert } from '@/helpers/bigInt';
import { formatAdjustedAmount, formatDollars, formatLtv } from '@/helpers/formatters';
import { getOraclePriceFeedDecimals } from '@/helpers/oracle';
import { orpc } from '@/lib/orpc/client';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import { getHasBorrowPosition, getHasLendPosition } from '../helpers/position';
import { InfoMyPositionDataListError, InfoMyPositionDataListLoading } from './states';
const percentageFormatter = formatters.percent.digits(2).default('-').unit('%');

/** The token's USD price as a WAD, or undefined when Core cannot price it. */
const toPriceWad = (token: PositionToken | null) => !token || token.priceUsd === null ? undefined : parseEther(token.priceUsd.toFixed(18));

/** The amount's USD value as a WAD, or null when the token has no price. */
const toUsdWad = (token: PositionToken | null, amount: bigint) => {
  const price = toPriceWad(token);
  if (!token || price === undefined) return null;
  return amount * price / 10n ** BigInt(token.decimals);
};
const toUsd = (token: PositionToken | null, amount: bigint) => {
  const usd = toUsdWad(token, amount);
  return usd === null ? null : Number(usd) / 1e18;
};
interface AssetAmountLineProps {
  label: string;
  token: PositionToken | null;
  amount: bigint;
  tooltip?: ReactNode;
}
const AssetAmountLine = t0 => {
  const $ = _c(38);
  const {
    label,
    token,
    amount,
    tooltip
  } = t0;
  let T0;
  let T1;
  let T2;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  if ($[0] !== amount || $[1] !== label || $[2] !== token || $[3] !== tooltip) {
    const usd = toUsdWad(token, amount);
    T2 = DataList.Line;
    if ($[12] !== label || $[13] !== tooltip) {
      t5 = <DataList.LeftItem label={label} tooltip={tooltip} />;
      $[12] = label;
      $[13] = tooltip;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    T1 = DataList.RightItem;
    t2 = "flex flex-row items-center gap-[2px]";
    const t6 = token?.logoUri ?? undefined;
    if ($[15] !== t6) {
      t3 = <Avatar src={t6} size="small" />;
      $[15] = t6;
      $[16] = t3;
    } else {
      t3 = $[16];
    }
    let t7;
    if ($[17] !== amount || $[18] !== token) {
      t7 = formatAdjustedAmount({
        price: toPriceWad(token),
        amount,
        decimals: token?.decimals ?? 18,
        withTrailingZero: false
      });
      $[17] = amount;
      $[18] = token;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    const t8 = token?.symbol;
    if ($[20] !== t7 || $[21] !== t8) {
      t4 = <span className="text-foreground text-xs">{t7}{" "}{t8}</span>;
      $[20] = t7;
      $[21] = t8;
      $[22] = t4;
    } else {
      t4 = $[22];
    }
    T0 = Tag;
    t1 = usd === null ? "-" : formatDollars(usd);
    $[0] = amount;
    $[1] = label;
    $[2] = token;
    $[3] = tooltip;
    $[4] = T0;
    $[5] = T1;
    $[6] = T2;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    T0 = $[4];
    T1 = $[5];
    T2 = $[6];
    t1 = $[7];
    t2 = $[8];
    t3 = $[9];
    t4 = $[10];
    t5 = $[11];
  }
  let t6;
  if ($[23] !== T0 || $[24] !== t1) {
    t6 = <T0 label={t1} />;
    $[23] = T0;
    $[24] = t1;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== t2 || $[27] !== t3 || $[28] !== t4 || $[29] !== t6) {
    t7 = <div className={t2}>{t3}{t4}{t6}</div>;
    $[26] = t2;
    $[27] = t3;
    $[28] = t4;
    $[29] = t6;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  let t8;
  if ($[31] !== T1 || $[32] !== t7) {
    t8 = <T1>{t7}</T1>;
    $[31] = T1;
    $[32] = t7;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  let t9;
  if ($[34] !== T2 || $[35] !== t5 || $[36] !== t8) {
    t9 = <T2>{t5}{t8}</T2>;
    $[34] = T2;
    $[35] = t5;
    $[36] = t8;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  return t9;
};
export interface InfoMyPositionDataListClientProps {
  chainId: number;
  marketId: MarketId;
  market: MarketPositionMarket;
  /** The position of the wallet in the SSR-time wagmi cookie, if any. */
  serverPosition: {
    userAddress: Address;
    data: MarketPosition;
  } | null;
}
const InfoMyPositionDataListClient = t0 => {
  const $ = _c(93);
  const {
    chainId,
    marketId,
    market,
    serverPosition
  } = t0;
  const {
    targetChainId,
    account
  } = useWeb3Context();
  const isAutoDeleverageEnabled = useIsAutoDeleverageEnabled(targetChainId);
  const {
    status: autoDeleverageStatus
  } = useAutoDeleverage(marketId, targetChainId);
  const userAddress = account.address;
  let t1;
  if ($[0] !== serverPosition || $[1] !== userAddress) {
    t1 = !!userAddress && !!serverPosition && isAddressEqual(serverPosition.userAddress, userAddress);
    $[0] = serverPosition;
    $[1] = userAddress;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isServerAccount = t1;
  let t2;
  if ($[3] !== chainId || $[4] !== isServerAccount || $[5] !== marketId || $[6] !== userAddress) {
    t2 = orpc.market.position.queryOptions({
      input: userAddress && !isServerAccount ? {
        chainId,
        marketId,
        userAddress
      } : skipToken
    });
    $[3] = chainId;
    $[4] = isServerAccount;
    $[5] = marketId;
    $[6] = userAddress;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const clientPositionQuery = useQuery(t2);
  const position = !userAddress ? null : isServerAccount ? serverPosition.data : clientPositionQuery.data ?? null;
  const positionState = position && {
    collateral: BigInt(position.collateralAssets),
    borrowShares: BigInt(position.borrowShares),
    borrowAssets: BigInt(position.borrowAssets),
    supplyShares: BigInt(position.supplyShares),
    supplyAssets: BigInt(position.supplyAssets)
  };
  const marketState = {
    totalSupplyAssets: BigInt(market.supplyAssets),
    totalBorrowAssets: BigInt(market.borrowAssets),
    totalBorrowShares: BigInt(market.borrowShares),
    price: market.oraclePrice === null ? null : BigInt(market.oraclePrice)
  };
  const {
    loanToken,
    collateralToken
  } = market;
  let t3;
  if ($[8] !== market.lltv || $[9] !== marketId || $[10] !== targetChainId) {
    t3 = {
      lltv: market.lltv,
      marketId,
      chainId: targetChainId
    };
    $[8] = market.lltv;
    $[9] = marketId;
    $[10] = targetChainId;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const protectedLltv = useProtectedLltv(t3);
  const liquidationPrice = marketState.totalBorrowAssets && marketState.totalBorrowShares && positionState?.collateral && positionState.borrowShares && protectedLltv ? MarketUtils.getLiquidationPrice(positionState, marketState, {
    lltv: protectedLltv
  }) : null;
  const ltv = positionState && marketState.price ? MarketUtils.getLtv(positionState, {
    ...marketState,
    price: marketState.price
  }) : null;
  const formattedLtv = formatLtv(ltv);
  const oracleDecimals = getOraclePriceFeedDecimals({
    loanTokenDecimals: loanToken.decimals,
    collateralTokenDecimals: collateralToken?.decimals ?? 18
  });
  const liquidationPriceInLoanAssetDecimals = convert(liquidationPrice ?? ZERO, BigInt(oracleDecimals), BigInt(loanToken.decimals));
  const formattedLiquidationPrice = formatters.commas.unit(`${loanToken.symbol} / ${collateralToken?.symbol}`).digits(getDigitsFromPrice(liquidationPriceInLoanAssetDecimals, loanToken.decimals)).trailingZero(false).default("-").of(liquidationPrice, oracleDecimals);
  const isPositionUnhealthy = !!ltv && !!protectedLltv && getIsPositionUnhealthy(ltv, protectedLltv);
  const formattedUtilization = percentageFormatter.of(MarketUtils.getUtilization(marketState), 18);
  const supplyAssets = positionState?.supplyAssets ?? ZERO;
  const {
    marketLendPosition: isMarketLendPositionEnabled
  } = useFeatureFlags();
  const showLendPosition = isMarketLendPositionEnabled && getHasLendPosition(positionState && {
    supplyShares: positionState.supplyShares,
    supplyAssetsUsd: toUsd(loanToken, supplyAssets)
  });
  const hasBorrowPosition = getHasBorrowPosition(positionState);
  const t4 = positionState?.borrowAssets ?? ZERO;
  let t5;
  if ($[12] !== loanToken || $[13] !== t4) {
    t5 = <AssetAmountLine label="Loan" token={loanToken} amount={t4} />;
    $[12] = loanToken;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const loanLine = t5;
  const t6 = positionState?.collateral ?? ZERO;
  let t7;
  if ($[15] !== collateralToken || $[16] !== t6) {
    t7 = <AssetAmountLine label="Collateral" token={collateralToken} amount={t6} />;
    $[15] = collateralToken;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const collateralLine = t7;
  let t8;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      lineHeight: "normal"
    };
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== marketId || $[20] !== targetChainId) {
    t9 = <span className="text-secondary-foreground text-xs" style={t8}><ProtectedLltvLabel defaultLabel="LTV / Liquidation LTV" marketId={marketId} chainId={targetChainId} /></span>;
    $[19] = marketId;
    $[20] = targetChainId;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== isAutoDeleverageEnabled || $[23] !== marketId || $[24] !== targetChainId) {
    t10 = isAutoDeleverageEnabled && <AutoDeleverageButton marketId={marketId} chainId={targetChainId} />;
    $[22] = isAutoDeleverageEnabled;
    $[23] = marketId;
    $[24] = targetChainId;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t9) {
    t11 = <DataList.LeftItem><div className="gap-sm flex items-center justify-between">{t9}{t10}</div></DataList.LeftItem>;
    $[26] = t10;
    $[27] = t9;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const t12 = isPositionUnhealthy ? "text-destructive-foreground" : "text-foreground";
  let t13;
  if ($[29] !== t12) {
    t13 = cn("text-xs", t12);
    $[29] = t12;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== formattedLtv || $[32] !== t13) {
    t14 = <span className={t13}>{formattedLtv}</span>;
    $[31] = formattedLtv;
    $[32] = t13;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  let t16;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = {
      display: "flex",
      alignItems: "center",
      lineHeight: "normal",
      gap: "2px"
    };
    t16 = <span>/</span>;
    $[34] = t15;
    $[35] = t16;
  } else {
    t15 = $[34];
    t16 = $[35];
  }
  let t17;
  if ($[36] !== market.lltv || $[37] !== marketId || $[38] !== targetChainId) {
    t17 = <span className="text-foreground text-xs" style={t15}>{t16}<ProtectedLltv lltv={market.lltv} marketId={marketId} chainId={targetChainId} /></span>;
    $[36] = market.lltv;
    $[37] = marketId;
    $[38] = targetChainId;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t14 || $[41] !== t17) {
    t18 = <DataList.RightItem><div className="flex flex-row items-center gap-[2px]">{t14}{t17}</div></DataList.RightItem>;
    $[40] = t14;
    $[41] = t17;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== t11 || $[44] !== t18) {
    t19 = <DataList.Line>{t11}{t18}</DataList.Line>;
    $[43] = t11;
    $[44] = t18;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  const ltvLine = t19;
  let t20;
  if ($[46] !== loanToken || $[47] !== supplyAssets) {
    t20 = <AssetAmountLine label="Lent" token={loanToken} amount={supplyAssets} tooltip="Assets lent directly to this market, not through a vault, earning interest from borrowers." />;
    $[46] = loanToken;
    $[47] = supplyAssets;
    $[48] = t20;
  } else {
    t20 = $[48];
  }
  const lendLine = t20;
  let t21;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <DataList.LeftItem><span className="text-secondary-foreground text-xs">Oracle liquidation price</span></DataList.LeftItem>;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  let t22;
  if ($[50] !== autoDeleverageStatus || $[51] !== formattedLiquidationPrice || $[52] !== liquidationPrice || $[53] !== marketId || $[54] !== targetChainId) {
    t22 = <DataList.Line>{t21}<DataList.RightItem>{autoDeleverageStatus !== "loading" ? <span className="text-foreground text-xs" style={{
          display: "flex",
          alignItems: "center",
          gap: "2px",
          lineHeight: "normal",
          width: "fit-content"
        }}>{!!liquidationPrice && <ProtectedValueIndicator marketId={marketId} chainId={targetChainId} />}{formattedLiquidationPrice}</span> : <Skeleton className="h-md w-[50px] rounded-[20px]" />}</DataList.RightItem></DataList.Line>;
    $[50] = autoDeleverageStatus;
    $[51] = formattedLiquidationPrice;
    $[52] = liquidationPrice;
    $[53] = marketId;
    $[54] = targetChainId;
    $[55] = t22;
  } else {
    t22 = $[55];
  }
  const liquidationPriceLine = t22;
  let t23;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <DataList.LeftItem label="Utilization" tooltip={"Share of supplied liquidity that\u2019s currently borrowed."} />;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== formattedUtilization) {
    t24 = <DataList.Line>{t23}<DataList.RightItem label={formattedUtilization} /></DataList.Line>;
    $[57] = formattedUtilization;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  const utilizationLine = t24;
  let t25;
  if ($[59] !== collateralLine || $[60] !== loanLine || $[61] !== ltvLine) {
    t25 = <DataList.Column>{loanLine}{collateralLine}{ltvLine}</DataList.Column>;
    $[59] = collateralLine;
    $[60] = loanLine;
    $[61] = ltvLine;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  let t26;
  if ($[63] !== liquidationPriceLine || $[64] !== utilizationLine) {
    t26 = <DataList.Column>{liquidationPriceLine}{utilizationLine}</DataList.Column>;
    $[63] = liquidationPriceLine;
    $[64] = utilizationLine;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  let t27;
  if ($[66] !== t25 || $[67] !== t26) {
    t27 = <DataList.Root fullWidth={true}>{t25}{t26}</DataList.Root>;
    $[66] = t25;
    $[67] = t26;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  const borrowPositionList = t27;
  if (clientPositionQuery.isError && !clientPositionQuery.data) {
    let t28;
    if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
      t28 = <InfoMyPositionDataListError />;
      $[69] = t28;
    } else {
      t28 = $[69];
    }
    return t28;
  }
  if (userAddress && !isServerAccount && clientPositionQuery.isPending) {
    let t28;
    if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
      t28 = <InfoMyPositionDataListLoading />;
      $[70] = t28;
    } else {
      t28 = $[70];
    }
    return t28;
  }
  if (showLendPosition && hasBorrowPosition) {
    let t28;
    if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
      t28 = <SubSectionTitle>Borrow position</SubSectionTitle>;
      $[71] = t28;
    } else {
      t28 = $[71];
    }
    let t29;
    if ($[72] !== borrowPositionList) {
      t29 = <div className="gap-sm flex flex-col">{t28}{borrowPositionList}</div>;
      $[72] = borrowPositionList;
      $[73] = t29;
    } else {
      t29 = $[73];
    }
    let t30;
    if ($[74] === Symbol.for("react.memo_cache_sentinel")) {
      t30 = <SubSectionTitle>Lend position</SubSectionTitle>;
      $[74] = t30;
    } else {
      t30 = $[74];
    }
    let t31;
    if ($[75] !== lendLine) {
      t31 = <DataList.Column>{lendLine}</DataList.Column>;
      $[75] = lendLine;
      $[76] = t31;
    } else {
      t31 = $[76];
    }
    let t32;
    if ($[77] === Symbol.for("react.memo_cache_sentinel")) {
      t32 = <DataList.Column />;
      $[77] = t32;
    } else {
      t32 = $[77];
    }
    let t33;
    if ($[78] !== t31) {
      t33 = <div className="gap-sm flex flex-col">{t30}<DataList.Root fullWidth={true}>{t31}{t32}</DataList.Root></div>;
      $[78] = t31;
      $[79] = t33;
    } else {
      t33 = $[79];
    }
    let t34;
    if ($[80] !== t29 || $[81] !== t33) {
      t34 = <div className="gap-lg flex flex-col" data-testid="market-your-position-info">{t29}{t33}</div>;
      $[80] = t29;
      $[81] = t33;
      $[82] = t34;
    } else {
      t34 = $[82];
    }
    return t34;
  }
  if (showLendPosition) {
    let t28;
    if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
      t28 = <SubSectionTitle>Lend position</SubSectionTitle>;
      $[83] = t28;
    } else {
      t28 = $[83];
    }
    let t29;
    if ($[84] !== lendLine) {
      t29 = <DataList.Column>{lendLine}</DataList.Column>;
      $[84] = lendLine;
      $[85] = t29;
    } else {
      t29 = $[85];
    }
    let t30;
    if ($[86] !== utilizationLine) {
      t30 = <DataList.Column>{utilizationLine}</DataList.Column>;
      $[86] = utilizationLine;
      $[87] = t30;
    } else {
      t30 = $[87];
    }
    let t31;
    if ($[88] !== t29 || $[89] !== t30) {
      t31 = <div className="gap-sm flex flex-col" data-testid="market-your-position-info">{t28}<DataList.Root fullWidth={true}>{t29}{t30}</DataList.Root></div>;
      $[88] = t29;
      $[89] = t30;
      $[90] = t31;
    } else {
      t31 = $[90];
    }
    return t31;
  }
  let t28;
  if ($[91] !== borrowPositionList) {
    t28 = <div data-testid="market-your-position-info">{borrowPositionList}</div>;
    $[91] = borrowPositionList;
    $[92] = t28;
  } else {
    t28 = $[92];
  }
  return t28;
};
export default InfoMyPositionDataListClient;